import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Users, MoreHorizontal, Eye, Check, X, Ban, RotateCcw, MessageCircle, Plus, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, Avatar, StatusBadge, Pager } from "@/components/common";
import { Sel, Table, td } from "@/components/admin-ui";
import { CategoryCards } from "@/pages/admin/Overview";
import { api, errMsg } from "@/lib/api";
import { inr, compact, fdate, ago } from "@/lib/format";

const SORTS = [["newest", "Newest"], ["oldest", "Oldest"], ["followers_desc", "Highest followers"], ["followers_asc", "Lowest followers"],
  ["views_desc", "Highest views"], ["views_asc", "Lowest views"], ["earnings_desc", "Highest earnings"]];
const STATUSES = [["", "All statuses"], ["PENDING", "Pending"], ["APPROVED", "Approved"], ["SUSPENDED", "Suspended"], ["REJECTED", "Rejected"]];

export async function setCreatorStatus(uid, status, done) {
  try {
    await api.post(`/admin/creators/${uid}/status`, { status });
    toast.success(`Creator ${status.toLowerCase()}`);
    done?.();
  } catch (e) { toast.error(errMsg(e)); }
}

function RowActions({ c, reload }) {
  const nav = useNavigate();
  const st = (s) => setCreatorStatus(c.user_id, s, reload);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="rounded-full" data-testid={`creator-actions-${c.user_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card border-border rounded-xl w-48">
        <DropdownMenuItem onClick={() => nav(`/admin/creators/${c.user_id}`)} data-testid={`action-view-${c.user_id}`}><Eye className="h-4 w-4 mr-2" />View / Edit</DropdownMenuItem>
        {c.status !== "APPROVED" && c.status !== "SUSPENDED" && <DropdownMenuItem onClick={() => st("APPROVED")} data-testid={`action-approve-${c.user_id}`}><Check className="h-4 w-4 mr-2" />Approve</DropdownMenuItem>}
        {c.status === "PENDING" && <DropdownMenuItem onClick={() => st("REJECTED")} data-testid={`action-reject-${c.user_id}`}><X className="h-4 w-4 mr-2" />Reject</DropdownMenuItem>}
        {c.status === "APPROVED" && <DropdownMenuItem onClick={() => st("SUSPENDED")} data-testid={`action-suspend-${c.user_id}`}><Ban className="h-4 w-4 mr-2" />Suspend</DropdownMenuItem>}
        {c.status === "SUSPENDED" && <DropdownMenuItem onClick={() => st("APPROVED")} data-testid={`action-reactivate-${c.user_id}`}><RotateCcw className="h-4 w-4 mr-2" />Reactivate</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav(`/admin/messages?c=${c.user_id}`)} data-testid={`action-message-${c.user_id}`}><MessageCircle className="h-4 w-4 mr-2" />Message</DropdownMenuItem>
        {c.status === "APPROVED" && <DropdownMenuItem onClick={() => nav(`/admin/deals?new=${c.user_id}`)} data-testid={`action-add-deal-${c.user_id}`}><Plus className="h-4 w-4 mr-2" />Add deal</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminCreators() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [qInput, setQInput] = useState("");
  const [f, setF] = useState({ q: "", category_id: params.get("category") || "", status: params.get("status") || "", sort: "newest",
    min_followers: "", max_followers: "", min_views: "", max_views: "", joined_from: "", joined_to: "", page: 1 });
  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setF((x) => ({ ...x, q: qInput, page: 1 })), 350); return () => clearTimeout(t); }, [qInput]);
  useEffect(() => { setF((x) => ({ ...x, category_id: params.get("category") || "", page: 1 })); }, [params]);
  const clean = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
  const { data, loading, error, reload } = useApi("/admin/creators", clean);
  const upd = (k) => (v) => setF({ ...f, [k]: v, page: 1 });
  const setCat = (v) => { setParams(v ? { category: v } : {}); };
  const activeCat = cats.find((c) => c.id === f.category_id);

  return (
    <div>
      <PageHeader eyebrow="Directory" title={activeCat ? `${activeCat.name} creators` : "Creators"} desc="Search, filter and manage every Instagram creator." />
      {!activeCat && cats.length > 0 && <div className="mb-8"><CategoryCards categories={cats} testprefix="creators-category-card" /></div>}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search name, @username or email" className="h-10 pl-10 rounded-xl bg-card" data-testid="creators-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Sel value={f.category_id} onChange={setCat} options={[["", "All categories"], ...cats.map((c) => [c.id, c.name])]} testid="creators-category-filter" />
          <Sel value={f.status} onChange={upd("status")} options={STATUSES} testid="creators-status-filter" />
          <Sel value={f.sort} onChange={upd("sort")} options={SORTS} testid="creators-sort" />
          <Button variant="outline" className="rounded-xl h-10" onClick={() => setShowFilters(!showFilters)} data-testid="creators-more-filters"><SlidersHorizontal className="h-4 w-4 mr-2" />Filters</Button>
        </div>
      </div>
      {showFilters && (
        <div className="uc-card p-4 grid grid-cols-2 md:grid-cols-6 gap-3 mb-4 fade-up" data-testid="creators-advanced-filters">
          {[["min_followers", "Min followers"], ["max_followers", "Max followers"], ["min_views", "Min avg views"], ["max_views", "Max avg views"]].map(([k, l]) => (
            <Input key={k} type="number" min={0} placeholder={l} value={f[k]} onChange={(e) => upd(k)(e.target.value)} className="h-10 rounded-xl bg-background" data-testid={`filter-${k}`} />
          ))}
          <Input type="date" value={f.joined_from} onChange={(e) => upd("joined_from")(e.target.value)} className="h-10 rounded-xl bg-background" data-testid="filter-joined-from" />
          <Input type="date" value={f.joined_to} onChange={(e) => upd("joined_to")(e.target.value)} className="h-10 rounded-xl bg-background" data-testid="filter-joined-to" />
        </div>
      )}
      {loading && !data ? <LoadingGrid count={5} className="space-y-2" h="h-14" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={Users} title={f.q || f.status || f.category_id ? "No creators match these filters." : "No creators have joined yet."} testid="creators-empty" /> : (
          <Table testid="creators-table" head={["Creator", "Followers", "Avg views", "Category", "Location", "Status", "Deals", "Earnings", "Joined", "Last active", ""]}>
            {data.items.map((c) => (
              <tr key={c.user_id} className="hover:bg-secondary/30 transition-colors" data-testid={`creator-row-${c.user_id}`}>
                <td className={td}>
                  <Link to={`/admin/creators/${c.user_id}`} className="flex items-center gap-3">
                    <Avatar src={c.profile_picture} name={c.full_name} className="h-9 w-9" />
                    <span><span className="block font-medium">{c.full_name}</span><span className="block text-xs text-muted-foreground">@{c.instagram_username}</span></span>
                  </Link>
                </td>
                <td className={`${td} num`}>{compact(c.followers)}</td>
                <td className={`${td} num`}>{compact(c.avg_reel_views)}</td>
                <td className={td}>{c.category_name}</td>
                <td className={`${td} text-muted-foreground`}>{c.location || "—"}</td>
                <td className={td}><StatusBadge status={c.status} testid={`creator-status-${c.user_id}`} /></td>
                <td className={`${td} num`}>{c.total_deals}</td>
                <td className={`${td} num`}>{inr(c.total_earnings)}</td>
                <td className={`${td} text-muted-foreground`}>{fdate(c.joined_at)}</td>
                <td className={`${td} text-muted-foreground`}>{ago(c.last_active)}</td>
                <td className={td}><RowActions c={c} reload={reload} /></td>
              </tr>
            ))}
          </Table>
        )}
      {data && <Pager page={f.page} total={data.total} limit={data.limit} onPage={(p) => setF({ ...f, page: p })} />}
    </div>
  );
}
